import type { StoryNavItem } from "@/components/customer-story";

interface ArticleSection {
  id: string;
  title?: string;
}

/**
 * Every titled section of an article, in document order.
 *
 * Labels are the headings verbatim. Both navs wrap them — the sidebar pill
 * grows taller, the mobile chip keeps its floor width — so clipping the string
 * only cut real words mid-syllable ("…we take it fr…") without saving space.
 *
 * Returns the full list: the scroll-spy that drives both navs can only track
 * the sections it is given, so a truncated list leaves the indicator stranded
 * on the last item for the rest of the article. The desktop sidebar applies
 * its own three-pill cap (`ARTICLE_SIDEBAR_NAV_LIMIT`) for its narrow column;
 * the mobile bar scrolls horizontally and shows them all.
 */
export function createArticleNavItems(
  sections: ArticleSection[],
  limit?: number
): StoryNavItem[] {
  const items = sections
    .filter((section): section is ArticleSection & { title: string } =>
      Boolean(section.title)
    )
    .map(({ id, title }) => ({ id, label: title }));

  return limit === undefined ? items : items.slice(0, limit);
}

export function formatArticleDate(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(date);
}

export function formatArticleCardDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
